import { Datepicker } from 'vanillajs-datepicker';
import cs from 'vanillajs-datepicker/locales/cs';

// Register Czech locale
Object.assign(Datepicker.locales, cs);

export async function initDatepicker(parentElem: HTMLElement | Document): Promise<void> {
  // Initialize date range pickers
  // Collect unique containers - both inputs in a range picker share the same parent,
  // so we deduplicate to avoid initializing DateRangePicker twice on the same element.
  const dateRangeContainers = new Set<HTMLElement>();
  parentElem.querySelectorAll<HTMLInputElement>('.date-input.date-input-range').forEach((item) => {
    const container = item.parentElement?.classList.contains('input-daterange')
      ? item.parentElement
      : item;
    dateRangeContainers.add(container as HTMLElement);
  });

  dateRangeContainers.forEach((containerOrParent) => {
    const inputs = containerOrParent.querySelectorAll<HTMLInputElement>('input');
    if (inputs.length < 2) return;

    const rangeOpts = {
      language: 'cs',
      autohide: true,
      format: 'd. m. yyyy',
      clearBtn: true,
    };

    const fromPicker = new Datepicker(inputs[0], rangeOpts);
    const toPicker = new Datepicker(inputs[1], rangeOpts);

    // When FROM is set after TO, push TO forward to match FROM.
    // When TO is set before FROM, push FROM back to match TO.
    let updating = false;
    inputs[0].addEventListener('changeDate', () => {
      if (updating) return;
      const fromDate = fromPicker.dates[0];
      const toDate = toPicker.dates[0];
      if (fromDate !== undefined && toDate !== undefined && fromDate > toDate) {
        updating = true;
        toPicker.setDate(fromDate);
        updating = false;
      }
    });
    inputs[1].addEventListener('changeDate', () => {
      if (updating) return;
      const fromDate = fromPicker.dates[0];
      const toDate = toPicker.dates[0];
      if (fromDate !== undefined && toDate !== undefined && toDate < fromDate) {
        updating = true;
        fromPicker.setDate(toDate);
        updating = false;
      }
    });

    // Debounce changeDate from both inputs into a single 'input' event dispatch
    // to prevent double fetches in the datatable filter.
    let rangeDebounceTimer: ReturnType<typeof setTimeout> | undefined;
    inputs.forEach((input) => {
      input.addEventListener('changeDate', () => {
        clearTimeout(rangeDebounceTimer);
        rangeDebounceTimer = setTimeout(() => {
          inputs[0].dispatchEvent(new Event('input', { bubbles: true }));
        }, 50);
      });
    });
  });

  // Initialize single date pickers (excluding range pickers)
  const datepickers = parentElem.querySelectorAll<HTMLInputElement>(
    '.date-input:not(.date-input-range)'
  );
  datepickers.forEach((item) => {
    new Datepicker(item, {
      language: 'cs',
      autohide: true,
      format: 'd. m. yyyy',
      clearBtn: true, // Show clear button in picker
    });

    // Dispatch 'input' event when date is selected or cleared via picker
    // This ensures datatable filter integration works properly (triggers debounced fetch)
    item.addEventListener('changeDate', () => {
      item.dispatchEvent(new Event('input', { bubbles: true }));
    });
  });
}
